import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp } from "lucide-react";
import { type FormEvent, useState } from "react";
import { Button } from "../../components/ui/Button";
import { useConfirm } from "../../components/ui/ConfirmDialog";
import { Field, TextInput } from "../../components/ui/Field";
import { PageTitle, Spinner } from "../../components/ui/misc";
import { usePrompt } from "../../components/ui/PromptDialog";
import { toast } from "../../components/ui/Toast";
import { ApiError } from "../../lib/api/client";
import { api } from "../../lib/api/endpoints";
import type { Space } from "../../lib/api/models";
import { qk, useSpaces } from "../../lib/queries";
import { plural } from "../clients/client-ui";
import { ArchivedDialog } from "./ArchivedDialog";
import { MembersDialog } from "./MembersDialog";
import { slugDraft, slugify } from "./slug";

const KIND_LABEL = { client: "Client", internal: "Handbook", templates: "Templates" } as const;
/** What a row says about a client: where it comes from, or that it is a leftover. */
const clientNote = (s: Space) => {
  if (s.kind !== "client") return null;
  if (s.archivedAt) return s.synced ? "Hidden: no longer in Gorelo" : "Archived before the Gorelo sync";
  return s.synced ? "From Gorelo" : "Not linked to Gorelo";
};
/** Deletable for good: an internal space, an unlinked client, a hidden client (the server checks the same). */
const deletable = (s: Space) => s.kind === "internal" || (s.kind === "client" && (!s.synced || Boolean(s.archivedAt)));
const message = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

function NewSpaceForm({ onCreated }: { onCreated: () => Promise<unknown> }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [restricted, setRestricted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [slugError, setSlugError] = useState<string | null>(null);
  const create = async (e: FormEvent) => {
    e.preventDefault();
    const finalSlug = slugify(slugTouched ? slug : name);
    setSlugError(null);
    if (!name.trim() || !finalSlug) {
      setError("Enter a name.");
      return;
    }
    try {
      await api.spaces.create({ name: name.trim(), slug: finalSlug, restricted });
      toast(`Topic “${name.trim()}” created.`, "success");
      setName("");
      setSlug("");
      setSlugTouched(false);
      setRestricted(false);
      setError(null);
      await onCreated();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError(null);
        setSlugError("This slug is already used by another topic or client.");
      } else setError(message(err, "The topic could not be created."));
    }
  };
  return (
    <form
      onSubmit={create}
      noValidate
      aria-label="New topic"
      className="grid gap-3 rounded-xl border border-line bg-surface p-5 sm:grid-cols-[1fr_1fr_auto_auto] sm:items-end"
    >
      <Field label="Name" error={error}>
        {(id, describedBy) => (
          <TextInput
            id={id}
            aria-describedby={describedBy}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (!slugTouched) setSlug(slugify(e.target.value));
            }}
          />
        )}
      </Field>
      <Field label="Slug" error={slugError}>
        {(id, describedBy) => (
          <TextInput
            id={id}
            aria-describedby={describedBy}
            invalid={Boolean(slugError)}
            className="font-mono"
            value={slug}
            onChange={(e) => {
              setSlug(slugDraft(e.target.value));
              setSlugTouched(true);
            }}
            onBlur={() => setSlug(slugify(slug))}
          />
        )}
      </Field>
      <label className="flex h-9 items-center gap-2 text-sm">
        <input type="checkbox" checked={restricted} onChange={(e) => setRestricted(e.target.checked)} /> Restricted
      </label>
      <Button type="submit">Create topic</Button>
    </form>
  );
}

export function SpacesAdmin() {
  const queryClient = useQueryClient();
  const { data: spaces, isLoading } = useSpaces();
  const { data: archivedSpaces = [] } = useQuery({ queryKey: qk.archivedSpaces, queryFn: api.spaces.listArchived });
  const { confirm, element: confirmDialog } = useConfirm();
  const { ask, element: promptDialog } = usePrompt();
  const [members, setMembers] = useState<Space | null>(null);
  const [archived, setArchived] = useState<Space | null>(null);

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: qk.spaces }),
      queryClient.invalidateQueries({ queryKey: qk.clients }),
      queryClient.invalidateQueries({ queryKey: qk.archivedSpaces }),
    ]);
  const attempt = async (s: Space, run: () => Promise<unknown>) => {
    try {
      await run();
    } catch (e) {
      toast(`${s.name} could not be updated: ${message(e, "unknown error")}`, "error");
    }
    await refresh();
  };
  const patch = (s: Space, change: Parameters<typeof api.spaces.update>[1]) =>
    attempt(s, () => api.spaces.update(s.id, change));
  const rename = async (s: Space) => {
    const next = await ask({
      title: `Rename ${s.name}`,
      label: "Name",
      initial: s.name,
      confirmLabel: "Rename",
      validate: (v) => (v.trim() ? null : "Enter a name."),
    });
    if (next) await attempt(s, () => api.spaces.update(s.id, { name: next.trim() }));
  };
  /** The server sets positions as given and shifts nothing, so a move swaps two positions (renumbering ties first). */
  const swap = async (list: Space[], i: number, j: number) => {
    const a = list[i];
    const b = list[j];
    if (!a || !b) return;
    try {
      if (a.position === b.position) {
        await Promise.all(list.map((x, k) => api.spaces.update(x.id, { position: k })));
        await api.spaces.update(a.id, { position: j });
        await api.spaces.update(b.id, { position: i });
      } else {
        await api.spaces.update(a.id, { position: b.position });
        await api.spaces.update(b.id, { position: a.position });
      }
    } catch (e) {
      toast(`The order could not be changed: ${message(e, "unknown error")}`, "error");
    }
    await refresh();
  };
  const archiveSpace = async (s: Space) => {
    const templates = s.kind === "templates";
    const ok = await confirm({
      title: `Archive ${s.name}?`,
      body: `${templates ? "Templates" : "The topic"} and its documents disappear for everyone except admins. You can restore it below.`,
      confirmLabel: templates ? "Archive Templates" : "Archive topic",
      danger: true,
    });
    if (ok) await patch(s, { archived: true });
  };
  /** An internal space, an unlinked client or a hidden client. A synced client that Docs shows cannot be deleted. */
  const deleteSpace = async (s: Space) => {
    const client = s.kind === "client";
    const ok = await confirm({
      title: `Delete ${s.name} for good?`,
      body: client
        ? "The client, every document in it with its history, and its sites, devices and network equipment are deleted. This cannot be undone."
        : "The topic and every document in it, with their history, are deleted. This cannot be undone. To keep them, archive the topic instead.",
      confirmLabel: client ? "Delete client" : "Delete topic",
      danger: true,
    });
    if (!ok) return;
    await attempt(s, async () => {
      const { documents, sites, items } = await api.spaces.remove(s.id);
      const docs = plural(documents, "document", "documents");
      toast(
        client
          ? `${s.name} deleted, with ${docs}, ${plural(sites, "site", "sites")} and ${plural(items, "item", "items")}.`
          : `${s.name} deleted, with ${docs}.`,
        "success",
      );
    });
  };
  const restore = (s: Space) =>
    attempt(s, async () => {
      await api.spaces.unarchive(s.id);
      toast(`${s.name} restored.`, "success");
    });

  const list = spaces ?? [];
  return (
    <div>
      <PageTitle>Topics and clients</PageTitle>
      <div className="mb-8 grid gap-4">
        <section aria-label="Create">
          <h2 className="mb-2 text-lg font-semibold text-ink">New topic</h2>
          <NewSpaceForm onCreated={refresh} />
          <p className="mt-2 text-sm text-ink-3">
            Clients are not made here: they come from Gorelo. See Admin, Gorelo for the sync.
          </p>
        </section>
      </div>

      {isLoading ? <Spinner label="Loading topics and clients…" /> : null}
      <ul className="grid gap-2">
        {list.map((s, i) => (
          <li
            key={s.id}
            className="flex flex-wrap items-center gap-3 rounded-lg border border-line bg-surface px-4 py-3"
          >
            <div className="min-w-48 flex-1">
              <div className="font-medium text-ink">{s.name}</div>
              <div className="flex gap-2 font-mono text-xs text-ink-3">
                <span>/{s.slug}</span>
                <span className="font-sans">{KIND_LABEL[s.kind]}</span>
                {clientNote(s) ? <span className="font-sans">{clientNote(s)}</span> : null}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                aria-label="Restricted"
                checked={s.restricted}
                onChange={(e) => void patch(s, { restricted: e.target.checked })}
              />
              Restricted
            </label>
            {s.restricted ? (
              <Button size="sm" onClick={() => setMembers(s)}>
                Members
              </Button>
            ) : null}
            <Button size="sm" onClick={() => setArchived(s)}>
              Archived documents
            </Button>
            {s.kind === "client" ? null : (
              <Button size="sm" variant="ghost" onClick={() => void rename(s)}>
                Rename
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              aria-label={`Move ${s.name} up`}
              disabled={i === 0}
              onClick={() => void swap(list, i, i - 1)}
            >
              <ArrowUp size={14} />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              aria-label={`Move ${s.name} down`}
              disabled={i === list.length - 1}
              onClick={() => void swap(list, i, i + 1)}
            >
              <ArrowDown size={14} />
            </Button>
            {s.kind === "client" ? null : (
              <Button size="sm" variant="danger" aria-label={`Archive ${s.name}`} onClick={() => void archiveSpace(s)}>
                Archive
              </Button>
            )}
            {deletable(s) ? (
              <Button size="sm" variant="danger" aria-label={`Delete ${s.name}`} onClick={() => void deleteSpace(s)}>
                Delete
              </Button>
            ) : null}
          </li>
        ))}
      </ul>

      {archivedSpaces.length > 0 ? (
        <section aria-label="Archived topics and clients" className="mt-8">
          <h2 className="mb-2 text-lg font-semibold text-ink">Archived topics and clients</h2>
          <ul className="grid gap-1">
            {archivedSpaces.map((s) => (
              <li key={s.id} className="flex items-center gap-3 rounded-md bg-column px-3 py-2 text-sm">
                <span className="flex-1 text-ink">
                  {s.name} <span className="text-xs text-ink-3">{clientNote(s) ?? KIND_LABEL[s.kind]}</span>
                </span>
                {s.kind === "client" ? null : (
                  <Button size="sm" aria-label={`Restore ${s.name}`} onClick={() => void restore(s)}>
                    Restore
                  </Button>
                )}
                {deletable(s) ? (
                  <Button
                    size="sm"
                    variant="danger"
                    aria-label={`Delete ${s.name}`}
                    onClick={() => void deleteSpace(s)}
                  >
                    Delete
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {members ? <MembersDialog space={members} open onClose={() => setMembers(null)} /> : null}
      {archived ? <ArchivedDialog space={archived} open onClose={() => setArchived(null)} /> : null}
      {confirmDialog}
      {promptDialog}
    </div>
  );
}
